import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Upload, FileText, X, AlertCircle, Loader2, CheckCircle2 } from 'lucide-react';
import { PageHeader } from '../components/common/PageHeader';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { requestsClient, unlockWallet, lockWallet } from '../auth/keycloakProvider';
import { isWalletUnlocked, getWalletAddress } from '../auth/walletSession';
import { uploadDocument } from '../api-clients';
import { SignAndSubmitBlockchainRequestCommand } from '../web-api-client';
import { useToast } from '../hooks/use-toast';
import { cn } from '../lib/utils';
import { getErrorToast } from '../lib/error-utils';

export default function ECMRAdd() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [file, setFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [signing, setSigning] = useState(false);
  const [unlocking, setUnlocking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUnlockModal, setShowUnlockModal] = useState(false);
  const [showSigningModal, setShowSigningModal] = useState(false);
  const [currentRequestUUID, setCurrentRequestUUID] = useState<string>('');
  const [currentDocumentId, setCurrentDocumentId] = useState<number | null>(null);
  const [walletUnlocked, setWalletUnlocked] = useState(false);
  const [walletAddress, setWalletAddress] = useState<string | null>(null);
  const [walletPassword, setWalletPassword] = useState('');
  const [xmlPreview, setXmlPreview] = useState<string>('');

  useEffect(() => {
    // Check wallet status on mount
    const checkWalletStatus = () => {
      const unlocked = isWalletUnlocked();
      setWalletUnlocked(unlocked);
      if (unlocked) {
        setWalletAddress(getWalletAddress());
      }
    };
    checkWalletStatus();
  }, []);

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile && droppedFile.name.endsWith('.xml')) {
      processFile(droppedFile);
    } else {
      toast({
        title: 'Erro',
        description: 'Por favor, carregue um ficheiro XML válido',
        variant: 'destructive',
      });
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      processFile(selectedFile);
    }
  };

  const processFile = async (selectedFile: File) => {
    if (!selectedFile.name.endsWith('.xml')) {
      toast({
        title: 'Erro',
        description: 'Por favor, carregue um ficheiro XML válido',
        variant: 'destructive',
      });
      return;
      }
    setFile(selectedFile);
    setError(null);

    try {
      const text = await selectedFile.text();
      // Preview first 500 characters
      setXmlPreview(text.substring(0, 500) + (text.length > 500 ? '...' : ''));
      
      // Validate XML
      const parser = new DOMParser();
      const xmlDoc = parser.parseFromString(text, 'text/xml');
      
      const parserError = xmlDoc.querySelector('parsererror');
      if (parserError) {
        throw new Error('Ficheiro XML inválido');
      }
    } catch (error) {
      setError('Erro ao processar o ficheiro XML. Verifique se o formato está correto.');
      setFile(null);
      setXmlPreview('');
    }
  };


  const handleRemoveFile = () => {
    setFile(null);
    setXmlPreview('');
    setError(null);
  };

  const handleUnlockWallet = async () => {
    if (!walletPassword) {
      setError('Por favor, introduza a password da wallet');
      return;
    }

    setUnlocking(true);
    setError(null);

    try {
      const session = await unlockWallet(walletPassword, 60); // 60 minutes session
      setWalletUnlocked(true);
      setWalletAddress(session.walletAddress);
      setShowUnlockModal(false);
      setWalletPassword('');
      toast({
        title: 'Sucesso',
        description: 'Wallet desbloqueada com sucesso!',
      });
      
      // Small delay to ensure session is fully stored and available
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // After unlocking, proceed with upload
      await performUpload();
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao desbloquear wallet');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setUnlocking(false);
    }
  };

  const performUpload = async () => {
    if (!file) return;

    setIsLoading(true);
    setError(null);

    try {
      // Read the file content
      const xmlContent = await file.text();
      
      // Upload document using the api-clients helper
      const result = await uploadDocument({
        xmlFile: file,
        xmlContent: xmlContent
      });
      
      // Store the request UUID and document ID for blockchain signing
      if (result.requestUUID) {
        setCurrentRequestUUID(result.requestUUID);
        setCurrentDocumentId(result.documentId);
        setShowSigningModal(true);
      } else {
        // If no requestUUID, the document was uploaded without blockchain signing
        toast({
          title: 'Sucesso',
          description: `eCMR carregado com sucesso! ID: ${result.documentId}`,
        });
        setTimeout(() => {
          navigate('/ecmrs');
        }, 2000);
      }
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao carregar eCMR');
      setError(err.data); // SMALL FIX. CHECK api-client.ts line 248
      toast({
        title: errorToast.title,
        description: errorToast.description,
        //description: err.data, // SMALL FIX. CHECK api-client.ts line 248
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

    const handleSubmit = async () => {
    if (!file) {
      toast({
        title: 'Erro',
        description: 'Por favor, selecione um ficheiro XML',
        variant: 'destructive',
      });
      return;
    }

    // Check if wallet is unlocked (backend wallet)
    if (!isWalletUnlocked()) {
      // Show unlock modal
      setShowUnlockModal(true);
      return;
    }

    // Wallet is unlocked, proceed with upload
    await performUpload();
  };

  const handleSignAndSubmit = async () => {
    if (!currentRequestUUID) return;

    setSigning(true);
    setError(null);

    try {
      await requestsClient.signAndSubmitBlockchainRequest(new SignAndSubmitBlockchainRequestCommand({
        requestUUID: currentRequestUUID
      }));
      toast({
        title: 'Sucesso',
        description: `eCMR carregado e registado na blockchain com sucesso! ID: ${currentDocumentId}`,
      });
      setShowSigningModal(false);
      setTimeout(() => {
        navigate('/ecmrs');
      }, 2000);
    } catch (err) {
      const errorToast = getErrorToast(err, 'Erro', 'Falha ao assinar e submeter transação blockchain');
      setError(errorToast.description);
      toast({
        title: errorToast.title,
        description: errorToast.description,
        variant: 'destructive',
      });
    } finally {
      setSigning(false);
    }
  };

  const handleCancelUpload = () => {
    setShowSigningModal(false);
    setCurrentRequestUUID('');
    setCurrentDocumentId(null);
    setError('Carregamento cancelado. O documento foi criado mas não foi registado na blockchain.');
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Adicionar eCMR"
        description="Carregue um ficheiro XML para criar um novo eCMR"
        backLink="/ecmrs"
      />

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Upload de Ficheiro XML
            </CardTitle>
            <CardDescription>
              Arraste e solte o ficheiro XML ou clique para selecionar
            </CardDescription>
          </CardHeader>
          <CardContent>
            {!file ? (
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={cn(
                  "border-2 border-dashed rounded-lg p-12 text-center transition-colors cursor-pointer",
                  isDragging ? "border-accent bg-accent/5" : "border-border hover:border-accent/50"
                )}
              >
                <input
                  type="file"
                  accept=".xml"
                  onChange={handleFileChange}
                  className="hidden"
                  id="xml-upload"
                />
                <label htmlFor="xml-upload" className="cursor-pointer">
                  <div className="flex flex-col items-center gap-4">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted">
                      <FileText className="h-8 w-8 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-medium">Clique para selecionar</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        ou arraste o ficheiro XML aqui
                      </p>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Formatos suportados: .xml
                    </p>
                  </div>
                </label>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10">
                      <FileText className="h-5 w-5 text-accent" />
                    </div>
                    <div>
                      <p className="font-medium text-sm">{file.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {(file.size / 1024).toFixed(2)} KB
                      </p>
                    </div>
                  </div>
                  <Button variant="ghost" size="icon" onClick={handleRemoveFile}>
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                {xmlPreview && (
                  <div className="mt-4">
                    <Label className="text-sm text-muted-foreground mb-2 block">Pré-visualização XML:</Label>
                    <pre className="border rounded-lg p-3 bg-muted/50 text-xs overflow-auto max-h-[200px]">
                      {xmlPreview}
                    </pre>
                  </div>
                )}

                {error && (
                  <div className="flex items-start gap-3 p-4 rounded-lg bg-destructive/10 text-destructive mt-4">
                    <AlertCircle className="h-5 w-5 mt-0.5" />
                    <p className="text-sm">{error}</p>
                  </div>
                )}

                {/* Wallet Status */}
                <div className="mt-4 p-4 rounded-lg bg-muted/50">
                  {walletUnlocked ? (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">🔓 Wallet Desbloqueada</p>
                        {walletAddress && (
                          <p className="text-xs text-muted-foreground">Endereço: {walletAddress.substring(0, 10)}...</p>
                        )}
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                          try {
                            await lockWallet();
                            setWalletUnlocked(false);
                            setWalletAddress(null);
                          } catch (err) {
                            console.error('Failed to lock wallet:', err);
                          }
                        }}
                      >
                        Bloquear
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium">🔒 Wallet Bloqueada</p>
                        <p className="text-xs text-muted-foreground">Será solicitado o desbloqueio ao submeter</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {file && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle2 className="h-5 w-5 text-accent" />
                Ficheiro Selecionado
              </CardTitle>
              <CardDescription>
                Verifique o ficheiro antes de submeter
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <div>
                  <p className="text-muted-foreground">Nome do Ficheiro</p>
                  <p className="font-medium">{file.name}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">Tamanho</p>
                  <p className="font-medium">{(file.size / 1024).toFixed(2)} KB</p>
                </div>
              </div>

              <div className="pt-4 border-t">
                <Button 
                  onClick={handleSubmit} 
                  className="w-full"
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      A carregar...
                    </>
                  ) : (
                    'Submeter eCMR'
                  )}
                </Button>
                {!walletUnlocked && (
                  <p className="text-xs text-muted-foreground mt-2 text-center">
                    Será solicitado o desbloqueio da wallet ao submeter
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Wallet Unlock Modal */}
      <Dialog open={showUnlockModal} onOpenChange={setShowUnlockModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>🔐 Desbloquear Wallet</DialogTitle>
            <DialogDescription>
              Introduza a password da wallet para desbloquear para operações blockchain.
              A wallet permanecerá desbloqueada durante 60 minutos.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="walletPassword">Password da Wallet</Label>
              <Input
                id="walletPassword"
                type="password"
                value={walletPassword}
                onChange={(e) => setWalletPassword(e.target.value)}
                placeholder="Introduza a password"
                onKeyPress={(e) => e.key === 'Enter' && handleUnlockWallet()}
              />
            </div>
            {error && (
              <div className="p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowUnlockModal(false);
              setError(null);
              setWalletPassword('');
            }} disabled={unlocking}>
              Cancelar
            </Button>
            <Button onClick={handleUnlockWallet} disabled={unlocking || !walletPassword}>
              {unlocking ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  A desbloquear...
                </>
              ) : (
                'Desbloquear Wallet'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signing Modal */}
      <Dialog open={showSigningModal} onOpenChange={setShowSigningModal}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Completar Registo na Blockchain</DialogTitle>
            <DialogDescription>
              O seu eCMR foi carregado e está pronto para ser registado na blockchain.
              Clique no botão abaixo para assinar e submeter a transação blockchain.
            </DialogDescription>
          </DialogHeader>
          <div className="py-8">
            {signing ? (
              <div className="flex flex-col items-center justify-center">
                <Loader2 className="h-12 w-12 animate-spin text-primary mb-4" />
                <p className="text-center text-muted-foreground">
                  A assinar e submeter para a blockchain...
                </p>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center">
                <div className="text-5xl mb-4">📄</div>
                <p className="text-center text-sm text-muted-foreground">
                  ID do Documento: {currentDocumentId}<br />
                  Request UUID: {currentRequestUUID}
                </p>
              </div>
            )}
            {error && (
              <div className="mt-4 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
                {error}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={handleCancelUpload} disabled={signing}>
              Cancelar
            </Button>
            <Button onClick={handleSignAndSubmit} disabled={signing}>
              {signing ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  A assinar...
                </>
              ) : (
                'Assinar e Submeter para Blockchain'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
